import React, { useState, useEffect, useRef } from 'react';
import {
    Box, TextField, Button, Typography, Link,
    Alert, CircularProgress, IconButton, InputAdornment,
    Chip, Avatar, LinearProgress
} from '@mui/material';
import {
    PersonOutlined as PersonIcon,
    LockOutlined as LockIcon,
    Visibility, VisibilityOff,
    ShieldOutlined as ShieldIcon,
    HttpsOutlined as HttpsIcon,
    VerifiedOutlined as VerifiedIcon,
    CheckCircle as CheckCircleIcon,
    AutoAwesome as AiIcon,
    ArrowForward as ArrowIcon,
    CloudUploadOutlined as UploadIcon,
    PsychologyOutlined as BrainIcon,
    PersonSearchOutlined as SearchIcon,
    DescriptionOutlined as DocIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import logoImage from '../Assets/Images/logo.png';
import person1 from '../Assets/Images/person1.jpg';
import person2 from '../Assets/Images/person2.jpg';
import { LoginApi } from '../Api/AuthPermissionsApi';
import ForgotPasswordModal from './ForgotPasswordModal';
import SignUp from './SignUp';
import CompanyDetails from '../Pages/CompanyDetails/CompanyDetails';
import { motion } from 'framer-motion';

/* ─── helpers ─── */
const extractTokenFromResponse = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    for (const [key, value] of Object.entries(payload)) {
        if (typeof value === 'string' && key.toLowerCase().includes('token')) return value;
        if (value && typeof value === 'object') {
            const n = extractTokenFromResponse(value);
            if (n) return n;
        }
    }
    return null;
};

const STEPS = [
    { id: 0, label: 'Upload JD', color: '#60A5FA' },
    { id: 1, label: 'AI Analyze', color: '#C084FC' },
    { id: 2, label: 'Scan Talent', color: '#FBBF24' },
    { id: 3, label: 'AI Match', color: '#34D399' },
];

/* ─────────────────────────────────────────────────────
   SLIDE COMPONENTS
───────────────────────────────────────────────────── */

function SlideUpload({ active }) {
    const [phase, setPhase] = useState('idle');

    useEffect(() => {
        if (!active) { setPhase('idle'); return; }
        const t1 = setTimeout(() => setPhase('flying'), 100);
        const t2 = setTimeout(() => setPhase('landed'), 900);
        const t3 = setTimeout(() => setPhase('scanning'), 1500);
        const t4 = setTimeout(() => setPhase('done'), 3200);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    }, [active]);

    const transforms = {
        idle: { opacity: 0, transform: 'scale(0.1) translateY(-200px) rotate(-10deg)', filter: 'blur(6px)' },
        flying: { opacity: 1, transform: 'scale(0.5) translateY(-60px) rotate(-4deg)', filter: 'blur(1px)' },
        landed: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
        scanning: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
        done: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
    };
    const current = transforms[phase] || transforms.idle;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <UploadIcon sx={{ color: '#60A5FA', fontSize: 16 }} />
                <Typography sx={{ color: '#60A5FA', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 1 · Upload Job Description
                </Typography>
            </Box>

            <Box sx={{
                border: phase === 'done' ? '1.5px solid rgba(52,211,153,0.4)' : '1.5px dashed rgba(96,165,250,0.35)',
                borderRadius: '10px', p: 2.2, mb: 1.5, minHeight: 110,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: phase === 'done' ? 'rgba(52,211,153,0.04)' : 'rgba(96,165,250,0.04)',
                transition: 'all 0.4s ease', position: 'relative', overflow: 'hidden'
            }}>
                {phase === 'scanning' && (
                    <Box sx={{
                        position: 'absolute', left: 0, right: 0, top: 0, height: '2px',
                        background: 'linear-gradient(90deg, transparent, #60A5FA, transparent)',
                        boxShadow: '0 0 8px #60A5FA',
                        animation: 'scanLine 1.5s ease-in-out forwards'
                    }} />
                )}

                <Box sx={{
                    ...current,
                    transition: 'transform 0.7s cubic-bezier(0.2, 1.4, 0.4, 1), opacity 0.5s ease, filter 0.4s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    animation: phase === 'done' ? 'floatFile 3s ease-in-out infinite' : 'none'
                }}>
                    <Box sx={{
                        width: 54, height: 64, borderRadius: '8px',
                        background: 'linear-gradient(145deg, #1E3A8A, #2563EB)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        boxShadow: ['landed', 'scanning', 'done'].includes(phase) ? '0 10px 28px rgba(37,99,235,0.5)' : '0 2px 8px rgba(0,0,0,0.3)',
                        transition: 'box-shadow 0.4s ease', position: 'relative', overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute', top: 0, left: '-60%', width: '40%', height: '100%',
                            background: 'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                            animation: phase === 'done' ? 'shimmer 2.5s linear infinite' : 'none',
                        }} />
                        <DocIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 24, mb: 0.2 }} />
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '9.5px', letterSpacing: 1 }}>PDF</Typography>
                        <Box sx={{ position: 'absolute', top: 0, right: 0, borderLeft: '10px solid transparent', borderBottom: '10px solid rgba(0,0,0,0.2)' }} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ color: '#CBD5E1', fontSize: '13px', fontWeight: 600, display: 'block' }}>Senior_FullStack_JD.pdf</Typography>
                        <Typography sx={{ color: '#475569', fontSize: '11px' }}>2.4 MB</Typography>
                    </Box>
                </Box>
            </Box>

            {(phase === 'scanning' || phase === 'done') && (
                <Box sx={{ animation: 'fadeUp 0.3s ease' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ color: '#64748B', fontSize: '12px' }}>
                            {phase === 'done' ? '✓ Upload complete' : 'Uploading & parsing…'}
                        </Typography>
                        <Typography sx={{ color: phase === 'done' ? '#34D399' : '#60A5FA', fontWeight: 700, fontSize: '12px' }}>
                            {phase === 'done' ? '100%' : '68%'}
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={phase === 'done' ? 100 : 68} sx={{
                        height: 5, borderRadius: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { backgroundColor: phase === 'done' ? '#34D399' : '#60A5FA', transition: 'transform 0.8s ease' }
                    }} />
                </Box>
            )}
        </Box>
    );
}

const AI_TEXT = 'Analyzing requirements for Senior FullStack Engineer…\n\nDetected: 6 core skills, 5+ years experience, remote-friendly role.';

function SlideAnalyze({ active }) {
    const [displayed, setDisplayed] = useState('');
    const [chips, setChips] = useState([]);
    const skills = ['React & Node', '5+ Years', 'AWS/Cloud', 'System Design', 'TypeScript', 'CI/CD'];
    const timerRef = useRef(null);

    useEffect(() => {
        if (!active) { setDisplayed(''); setChips([]); return; }
        let idx = 0;
        timerRef.current = setInterval(() => {
            idx++;
            setDisplayed(AI_TEXT.slice(0, idx));
            if (idx >= AI_TEXT.length) {
                clearInterval(timerRef.current);
                skills.forEach((_, i) => setTimeout(() => setChips(p => [...p, i]), i * 50 + 30));
            }
        }, 14);
        return () => clearInterval(timerRef.current);
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <BrainIcon sx={{ color: '#C084FC', fontSize: 16 }} />
                <Typography sx={{ color: '#C084FC', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 2 · AI Skill Extraction
                </Typography>
            </Box>

            <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px',
                border: '1px solid rgba(192,132,252,0.18)',
                p: 2, mb: 1.5,
                height: 135,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.2 }}>
                    {[['#F87171', 0.5], ['#FBBF24', 0.5], ['#34D399', 0.5]].map(([c, o], i) => (
                        <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c, boxShadow: `0 0 4px ${c}${Math.round(o * 255).toString(16)}` }} />
                    ))}
                    <Typography sx={{ color: '#334155', fontSize: '10px', ml: 0.5, fontFamily: 'monospace' }}>rono-ai ~ analyze</Typography>
                </Box>
                <Typography sx={{
                    color: '#A5F3FC', fontSize: '12.5px', lineHeight: 1.7,
                    whiteSpace: 'pre-wrap', display: 'block',
                    fontFamily: "'Fira Code', 'Courier New', monospace"
                }}>
                    <Box component="span" sx={{ color: '#818CF8' }}>$ </Box>
                    {displayed}
                    {displayed.length < AI_TEXT.length && (
                        <Box component="span" sx={{ animation: 'blink 0.65s step-start infinite', color: '#C084FC' }}>▌</Box>
                    )}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                {skills.map((s, i) => (
                    <Chip key={i} label={s} size="small" sx={{
                        backgroundColor: 'rgba(192,132,252,0.15)', color: '#E9D5FF',
                        fontWeight: 600, fontSize: '11px', border: '1px solid rgba(192,132,252,0.25)',
                        opacity: chips.includes(i) ? 1 : 0,
                        transform: chips.includes(i) ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.9)',
                        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                ))}
            </Box>
        </Box>
    );
}

function SlideScan({ active }) {
    const [count, setCount] = useState(0);
    const [found, setFound] = useState([]);
    const names = ['Rahul S.', 'Priya M.', 'Carlos D.', 'Ayesha K.'];

    useEffect(() => {
        if (!active) { setCount(0); setFound([]); return; }
        const target = 25420;
        const step = Math.ceil(target / 60);
        const t = setInterval(() => {
            setCount(p => {
                if (p + step >= target) { clearInterval(t); return target; }
                return p + step;
            });
        }, 45);
        names.forEach((_, i) => setTimeout(() => setFound(p => [...p, i]), i * 500 + 800));
        return () => clearInterval(t);
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <SearchIcon sx={{ color: '#FBBF24', fontSize: 16 }} />
                <Typography sx={{ color: '#FBBF24', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 3 · Talent Pool Scan
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.2, mb: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress size={48} thickness={2.5} variant="determinate" value={100} sx={{ color: 'rgba(255,255,255,0.07)', position: 'absolute' }} />
                    <CircularProgress size={48} thickness={2.5} sx={{ color: '#FBBF24' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 800, color: '#F1F5F9', fontSize: '23px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {count.toLocaleString()}
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '12px' }}>profiles scanned</Typography>
                </Box>
            </Box>

            <LinearProgress variant="indeterminate" sx={{
                height: 3, borderRadius: 2, mb: 1.5,
                backgroundColor: 'rgba(255,255,255,0.07)',
                '& .MuiLinearProgress-bar': { backgroundColor: '#FBBF24' }
            }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                {names.map((n, i) => (
                    <Chip key={i} label={`✓ ${n}`} size="small" sx={{
                        backgroundColor: 'rgba(251,191,36,0.1)', color: '#FDE68A',
                        fontWeight: 600, fontSize: '11px', border: '1px solid rgba(251,191,36,0.2)',
                        opacity: found.includes(i) ? 1 : 0,
                        transform: found.includes(i) ? 'scale(1)' : 'scale(0.75)',
                        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)'
                    }} />
                ))}
            </Box>
        </Box>
    );
}

function SlideResults({ active }) {
    const [shown, setShown] = useState([]);
    const candidates = [
        { name: 'Rahul Sharma', role: 'Sr. FullStack · 6 yrs', match: 98, img: person1 },
        { name: 'Priya Mehta', role: 'React Lead · 5 yrs', match: 95, img: person2 },
    ];

    useEffect(() => {
        if (!active) { setShown([]); return; }
        candidates.forEach((_, i) => setTimeout(() => setShown(p => [...p, i]), i * 400 + 150));
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <AiIcon sx={{ color: '#34D399', fontSize: 16 }} />
                <Typography sx={{ color: '#34D399', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 4 · AI Ranked Matches
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 1.5 }}>
                {candidates.map((c, i) => (
                    <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        backgroundColor: i === 0 ? 'rgba(52,211,153,0.07)' : 'rgba(255,255,255,0.04)',
                        border: i === 0 ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '9px', p: 1.4, px: 1.6,
                        opacity: shown.includes(i) ? 1 : 0,
                        transform: shown.includes(i) ? 'translateY(0)' : 'translateY(16px)',
                        transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Avatar src={c.img} sx={{ width: 34, height: 34 }} />
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#F1F5F9', fontSize: '13.5px' }}>{c.name}</Typography>
                                <Typography sx={{ color: '#64748B', fontSize: '11px' }}>{c.role}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontWeight: 800, color: '#34D399', fontSize: '17px', lineHeight: 1 }}>{c.match}%</Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '10px' }}>match</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{
                p: 1.4, borderRadius: '8px',
                backgroundColor: 'rgba(52,211,153,0.06)',
                border: '1px solid rgba(52,211,153,0.15)',
                animation: active ? 'fadeUp 0.5s 0.8s both ease' : 'none'
            }}>
                <Typography sx={{ color: '#6EE7B7', fontSize: '12px' }}>
                    🎯 AI shortlisted 2 of 25,420 profiles — 95%+ confidence.
                </Typography>
            </Box>
        </Box>
    );
}

/* ─────────────────────────────────────────────────────
   MAIN LOGIN
───────────────────────────────────────────────────── */
const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isLogin = location.pathname !== '/signup';

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        let delay = 5500;
        if (activeStep === 0) delay = 5000;
        else if (activeStep === 1) delay = 3800; // Further reduced to 3.8s for maximum snappiness
        else if (activeStep === 2) delay = 5500;
        else if (activeStep === 3) delay = 6500;

        const t = setTimeout(() => {
            setActiveStep(prev => (prev + 1) % 4);
        }, delay);
        return () => clearTimeout(t);
    }, [activeStep]);

    useEffect(() => {
        if (new URLSearchParams(location.search).get('sessionExpired') === 'true')
            setError('Your session has expired. Please log in again.');
    }, [location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        if (!username.trim()) { setError('Please enter your email or mobile number'); return; }
        if (!password.trim()) { setError('Please enter your password'); return; }
        setLoading(true);
        try {
            const response = await LoginApi({ userName: username.trim(), password });
            const token = extractTokenFromResponse(response);
            if (!token) { setError('Login succeeded but no token returned. Contact support.'); return; }
            localStorage.setItem('authToken', token);
            localStorage.setItem('token', token);
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.removeItem('hasCompany');
            const eid = response.employerId ?? response.data?.employerId ?? response.employer?.id ?? response.data?.employer?.id;
            if (eid != null) localStorage.setItem('employerId', String(eid));
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Login failed. Check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    /* ─── left panel content width = 88% of its container ─── */
    const CONTENT_W = '88%';

    return (
        <Box sx={{
            height: { xs: 'auto', md: '100vh' },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            overflow: 'hidden',
            position: 'relative'
        }}>
            {/* ── keyframes ── */}
            <style>{`
                * { font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif; }

                @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0} }
                @keyframes scanLine { 0%{top:0%;opacity:1} 100%{top:100%;opacity:0} }
                @keyframes fadeUp   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
                @keyframes tickPop  { 0%{transform:scale(0) rotate(-15deg);opacity:0} 65%{transform:scale(1.2) rotate(3deg);opacity:1} 100%{transform:scale(1) rotate(0)} }
                @keyframes glowPulse{ 0%,100%{opacity:.35;transform:scale(1)} 50%{opacity:.6;transform:scale(1.04)} }
                @keyframes shimmer  { 0%{left:-60%} 100%{left:120%} }
                @keyframes floatFile{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
                @keyframes ringPulse{ 0%{box-shadow:0 0 0 0 rgba(167,139,250,.55)} 100%{box-shadow:0 0 0 7px rgba(167,139,250,0)} }
            `}</style>

            {/* ══════════════════════════════════════════════════
                SIGNUP PANEL (white, register form on left side)
            ══════════════════════════════════════════════════ */}
            <Box sx={{
                width: { xs: '100%', md: '50%' },
                height: { md: '100vh' },
                minHeight: '100vh',
                backgroundColor: '#FFFFFF',
                display: { xs: isLogin ? 'none' : 'flex', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                overflowY: { md: 'auto' },
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            }}>
                <SignUp onlyForm={true} />
            </Box>

            {/* ══════════════════════════════════════════════════
                LEFT PANEL  (dark, animated)
            ══════════════════════════════════════════════════ */}
            <Box sx={{
                width: { xs: '100%', md: '50%' },
                height: { md: '100vh' },
                maxHeight: { md: '100vh' },
                minHeight: { md: '100vh' },
                boxSizing: 'border-box',
                /* dark purple gradient */
                background: 'linear-gradient(160deg, #070514 0%, #110E2A 55%, #2A1868 100%)',
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',        /* horizontally center children */
                p: { md: '40px 30px', lg: '50px 40px' },
                position: 'absolute',
                top: 0,
                left: { md: isLogin ? '0%' : '50%' },
                overflow: 'hidden',
                color: '#fff',
                zIndex: 10,
                transition: 'left 0.6s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                borderTopRightRadius: { md: isLogin ? '30px' : '0px' },
                borderBottomRightRadius: { md: isLogin ? '30px' : '0px' },
                borderTopLeftRadius: { md: isLogin ? '0px' : '30px' },
                borderBottomLeftRadius: { md: isLogin ? '0px' : '30px' },
            }}>
                {/* glow blobs */}
                <Box sx={{
                    position: 'absolute', bottom: -80, right: -80, width: 400, height: 400, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(109,40,217,.22) 0%, transparent 70%)',
                    animation: 'glowPulse 4s ease-in-out infinite', pointerEvents: 'none'
                }} />
                <Box sx={{
                    position: 'absolute', top: -50, left: -50, width: 250, height: 250, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(96,165,250,.09) 0%, transparent 70%)', pointerEvents: 'none'
                }} />

                {/* ── TOP: logo ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
                    <Box component="img" src={logoImage} alt="RONO Jobs"
                        sx={{ height: 28, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                    <Box
                        sx={{
                            position: 'relative',
                            padding: '2px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 16px rgba(192, 132, 252, 0.4), 0 0 4px rgba(96, 165, 250, 0.2)',
                            isolation: 'isolate',
                            transform: 'translateZ(0)',
                        }}
                    >
                        {/* Glowing Blur Aura Layer */}
                        <motion.div
                            style={{
                                position: 'absolute',
                                width: '300%',
                                height: '300%',
                                background: 'conic-gradient(from 0deg, transparent 25%, #60A5FA 40%, #C084FC 50%, transparent 50%)',
                                top: '-100%',
                                left: '-100%',
                                filter: 'blur(4px)',
                                opacity: 0.8,
                            }}
                            animate={{ rotate: 360 }}
                            transition={{
                                repeat: Infinity,
                                duration: 3.5,
                                ease: "linear"
                            }}
                        />
                        {/* Sharp Beam Layer with White Tip */}
                        <motion.div
                            style={{
                                position: 'absolute',
                                width: '300%',
                                height: '300%',
                                background: 'conic-gradient(from 0deg, transparent 30%, #60A5FA 45%, #C084FC 49%, #FFFFFF 50%, transparent 50%)',
                                top: '-100%',
                                left: '-100%',
                            }}
                            animate={{ rotate: 360 }}
                            transition={{
                                repeat: Infinity,
                                duration: 3.5,
                                ease: "linear"
                            }}
                        />
                        <Box
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 2,
                                height: 26,
                                borderRadius: '6px',
                                backgroundColor: '#0B081F',
                                color: '#DDD6FE',
                                fontWeight: 700,
                                fontSize: '12px',
                                letterSpacing: '1px',
                                zIndex: 1,
                            }}
                        >
                            <AiIcon sx={{ fontSize: '14px !important', color: '#C084FC !important' }} />
                            AI RECRUITER
                        </Box>
                    </Box>
                </Box>

                {/* ── MIDDLE: heading + stepper + carousel ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', zIndex: 1, mt: -6 }}>

                    <Typography sx={{
                        fontWeight: 800, color: '#F8FAFC', mb: 1.4, lineHeight: 1.25,
                        fontSize: { md: '30px', lg: '34px' }, letterSpacing: '-.6px'
                    }}>
                        Find Top Talent with AI,<br />
                        <span style={{ color: '#A78BFA' }}>In Seconds — Not Weeks</span>
                    </Typography>
                    <Typography sx={{ color: '#64748B', mb: 3.5, fontSize: '16.5px', lineHeight: 1.7 }}>
                        Upload a JD and our AI parses it, scans your talent pool, and surfaces the best matches instantly.
                    </Typography>

                    {/* ── Stepper ── */}
                    <Box sx={{ width: '100%', mb: 2, position: 'relative' }}>
                        {/* track */}
                        <Box sx={{
                            position: 'absolute', top: 15,
                            left: 'calc(12.5% + 15px)', right: 'calc(12.5% + 15px)',
                            height: 2, backgroundColor: 'rgba(255,255,255,.1)', zIndex: 0
                        }} />
                        {/* fill */}
                        <Box sx={{
                            position: 'absolute', top: 15, left: 'calc(12.5% + 15px)',
                            width: activeStep === 0 ? '0%' : `${Math.round((activeStep / 3) * 75)}%`,
                            height: 2, background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
                            transition: 'width .7s cubic-bezier(.4,0,.2,1)', zIndex: 0
                        }} />
                        {/* circles */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                            {STEPS.map(s => {
                                const done = s.id <= activeStep;
                                const current = s.id === activeStep;
                                return (
                                    <Box key={s.id} onClick={() => setActiveStep(s.id)}
                                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                                        <Box sx={{
                                            width: 30, height: 30, borderRadius: '50%', mb: .5,
                                            /* solid bg — line NEVER bleeds through */
                                            backgroundColor: done ? '#7C3AED' : '#0D0B1F',
                                            border: current ? `2px solid ${s.color}` : done ? '2px solid #7C3AED' : '2px solid rgba(255,255,255,.14)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all .4s cubic-bezier(.4,0,.2,1)',
                                            animation: current ? 'ringPulse 1.4s ease-out infinite' : 'none',
                                            boxShadow: done && !current ? '0 2px 10px rgba(124,58,237,.45)' : 'none',
                                            zIndex: 2, position: 'relative'
                                        }}>
                                            {done
                                                ? <CheckCircleIcon sx={{ color: '#fff', fontSize: 15, animation: 'tickPop .35s ease' }} />
                                                : <Typography sx={{ color: 'rgba(255,255,255,.4)', fontSize: '10px', fontWeight: 700 }}>{s.id + 1}</Typography>
                                            }
                                        </Box>
                                        <Typography sx={{
                                            fontSize: '10.5px', textAlign: 'center', lineHeight: 1.3,
                                            fontWeight: current ? 700 : 400,
                                            color: done ? '#CBD5E1' : 'rgba(255,255,255,.28)',
                                            transition: 'color .35s ease'
                                        }}>
                                            {s.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ── Carousel card ── */}
                    <Box sx={{
                        width: '100%', borderRadius: '14px',
                        border: '1px solid rgba(255,255,255,.09)',
                        backgroundColor: 'rgba(255,255,255,.035)',
                        backdropFilter: 'blur(12px)',
                        overflow: 'hidden',
                        boxShadow: '0 4px 28px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.07)'
                    }}>
                        {/* track */}
                        <Box sx={{
                            display: 'flex', width: '400%',
                            transform: `translateX(-${activeStep * 25}%)`,
                            transition: 'transform .65s cubic-bezier(.4,0,.2,1)'
                        }}>
                            {[
                                <SlideUpload active={activeStep === 0} />,
                                <SlideAnalyze active={activeStep === 1} />,
                                <SlideScan active={activeStep === 2} />,
                                <SlideResults active={activeStep === 3} />,
                            ].map((slide, i) => (
                                <Box key={i} sx={{ width: '25%', p: 2.5, boxSizing: 'border-box', flexShrink: 0 }}>
                                    {slide}
                                </Box>
                            ))}
                        </Box>
                        {/* dots */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: .8, py: 1.2, borderTop: '1px solid rgba(255,255,255,.06)' }}>
                            {STEPS.map(s => (
                                <Box key={s.id} onClick={() => setActiveStep(s.id)} sx={{
                                    width: activeStep === s.id ? 20 : 6, height: 6, borderRadius: 3,
                                    backgroundColor: activeStep === s.id ? '#7C3AED' : 'rgba(255,255,255,.18)',
                                    cursor: 'pointer', transition: 'all .35s ease'
                                }} />
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* ── BOTTOM: footer ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', zIndex: 1 }}>
                    <Typography sx={{ fontSize: '10.5px', color: 'rgba(255,255,255,.2)' }}>
                        © {new Date().getFullYear()} RONO Jobs™ · Powered by AI
                    </Typography>
                </Box>
            </Box>

            {/* ══════════════════════════════════════════════════
                RIGHT PANEL  (white, login form)
            ══════════════════════════════════════════════════ */}
            <Box sx={{
                width: { xs: '100%', md: '50%' },
                height: { md: '100vh' },
                minHeight: '100vh',
                backgroundColor: '#FFFFFF',
                display: { xs: isLogin ? 'flex' : 'none', md: 'flex' },
                alignItems: location.pathname === '/company-details' ? 'flex-start' : 'center',
                justifyContent: 'center',
                px: { xs: 3, sm: 5, md: 6 },
                overflowY: { md: 'auto' },
                boxSizing: 'border-box',
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            }}>
                {location.pathname === '/company-details' ? (
                    <CompanyDetails onlyForm={true} />
                ) : (
                    <Box sx={{ width: '100%', maxWidth: 380, position: 'relative' }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -20,
                                    left: -20,
                                    right: -20,
                                    bottom: -20,
                                    zIndex: 99,
                                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                    backdropFilter: 'blur(5px)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '16px',
                                    animation: 'fadeIn 0.25s ease-out',
                                    '@keyframes fadeIn': {
                                        '0%': { opacity: 0 },
                                        '100%': { opacity: 1 }
                                    }
                                }}
                            >
                                <CircularProgress
                                    size={44}
                                    thickness={4}
                                    sx={{
                                        color: '#6B31C4',
                                        mb: 2,
                                    }}
                                />
                                <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
                                    Signing in to RONO Jobs...
                                </Typography>
                            </Box>
                        )}
                        {/* mobile logo */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
                            <Box component="img" src={logoImage} alt="RONO Jobs" sx={{ height: 26, objectFit: 'contain' }} />
                        </Box>

                        {/* heading */}
                        <Typography sx={{ fontWeight: 800, color: '#0F172A', fontSize: { xs: '22px', sm: '26px' }, letterSpacing: '-.5px', lineHeight: 1.15, mb: 0.5 }}>
                            Welcome to RONO Jobs
                        </Typography>
                        <Typography sx={{ color: '#94A3B8', fontSize: '13.5px', mb: 3.5 }}>
                            Find the right talent. Hire faster. Grow your team.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px', fontSize: '13px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
                                {error}
                            </Alert>
                        )}

                        <form onSubmit={handleLogin}>
                            {/* email */}
                            <Box sx={{ mb: 2.2 }}>
                                <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: '12.5px', display: 'block', mb: 0.6 }}>
                                    Email or Mobile
                                </Typography>
                                <TextField fullWidth placeholder="you@company.com" value={username}
                                    onChange={e => { setUsername(e.target.value); setError(''); }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9CA3AF', fontSize: 18 }} /></InputAdornment>,
                                        sx: {
                                            borderRadius: '10px', height: 46, fontSize: '13.5px',
                                            backgroundColor: '#F9FAFB',
                                            '& fieldset': { borderColor: '#E5E7EB' },
                                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                                            '&.Mui-focused': { backgroundColor: '#fff', '& fieldset': { borderColor: '#7C3AED', borderWidth: '1.5px' } }
                                        }
                                    }}
                                />
                            </Box>

                            {/* password */}
                            <Box sx={{ mb: 0.8 }}>
                                <Typography sx={{ color: '#374151', fontWeight: 600, fontSize: '12.5px', display: 'block', mb: 0.6 }}>
                                    Password
                                </Typography>
                                <TextField fullWidth type={showPassword ? 'text' : 'password'} placeholder="Enter your password"
                                    value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#9CA3AF', fontSize: 18 }} /></InputAdornment>,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowPassword(p => !p)} edge="end" size="small" sx={{ color: '#9CA3AF' }}>
                                                    {showPassword ? <VisibilityOff sx={{ fontSize: 17 }} /> : <Visibility sx={{ fontSize: 17 }} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: '10px', height: 46, fontSize: '13.5px',
                                            backgroundColor: '#F9FAFB',
                                            '& fieldset': { borderColor: '#E5E7EB' },
                                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                                            '&.Mui-focused': { backgroundColor: '#fff', '& fieldset': { borderColor: '#7C3AED', borderWidth: '1.5px' } }
                                        }
                                    }}
                                />
                            </Box>

                            {/* forgot */}
                            <Box sx={{ textAlign: 'right', mb: 2.8 }}>
                                <Link underline="hover" onClick={() => setForgotOpen(true)}
                                    sx={{ color: '#7C3AED', fontWeight: 600, fontSize: '12.5px', cursor: 'pointer' }}>
                                    Forgot password?
                                </Link>
                            </Box>

                            {/* sign in button */}
                            <Button type="submit" fullWidth variant="contained" disabled={loading}
                                endIcon={!loading && <ArrowIcon sx={{ fontSize: '17px !important' }} />}
                                sx={{
                                    background: 'linear-gradient(90deg, #5B21B6 0%, #7C3AED 50%, #8B5CF6 100%)',
                                    backgroundSize: '200% auto', color: '#FFFFFF', height: 50, mb: 2.8,
                                    borderRadius: '11px', fontWeight: 700, fontSize: '14.5px', textTransform: 'none',
                                    boxShadow: '0 4px 16px rgba(109,40,217,.38)',
                                    transition: 'all 0.25s ease',
                                    '&:hover': { backgroundPosition: 'right center', boxShadow: '0 8px 24px rgba(109,40,217,.5)', transform: 'translateY(-1.5px)' },
                                    '&:active': { transform: 'translateY(0)', boxShadow: '0 3px 10px rgba(109,40,217,.4)' },
                                    '&:disabled': { background: '#E5E7EB', color: '#9CA3AF', boxShadow: 'none' }
                                }}
                            >
                                {loading
                                    ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                        <span>Signing in…</span>
                                    </Box>
                                    : 'Sign In'}
                            </Button>
                        </form>

                        {/* divider */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.8 }}>
                            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F1F5F9' }} />
                            <Typography sx={{ color: '#CBD5E1', fontSize: '12px' }}>or</Typography>
                            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#F1F5F9' }} />
                        </Box>

                        {/* sign up */}
                        <Box sx={{
                            textAlign: 'center', p: 1.8, borderRadius: '11px',
                            backgroundColor: '#FAFAFA', border: '1px solid #F1F5F9',
                            mb: 4,
                            transition: 'border-color .2s ease', '&:hover': { borderColor: '#E2E8F0' }
                        }}>
                            <Typography sx={{ color: '#64748B', fontSize: '13px' }}>
                                New to RONO Jobs?{' '}
                                <Link component="button" type="button" onClick={() => navigate('/signup')}
                                    sx={{
                                        color: '#7C3AED', fontWeight: 700, fontSize: '13px', cursor: 'pointer', textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline', color: '#6D28D9' }
                                    }}>
                                    Create an account
                                </Link>
                            </Typography>
                        </Box>

                        {/* trust row — forced single line */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.8, flexWrap: 'nowrap' }}>
                            {[
                                { icon: <ShieldIcon sx={{ fontSize: 11, color: '#6EE7B7' }} />, label: 'Secure Login' },
                                { icon: <HttpsIcon sx={{ fontSize: 11, color: '#6EE7B7' }} />, label: 'TLS Encrypted' },
                                { icon: <VerifiedIcon sx={{ fontSize: 11, color: '#6EE7B7' }} />, label: 'Verified' },
                            ].map((b, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#E2E8F0', flexShrink: 0 }} />}
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', gap: .4, flexShrink: 0,
                                        px: .9, py: .4, borderRadius: '20px',
                                        backgroundColor: 'rgba(16,185,129,.06)',
                                        border: '1px solid rgba(16,185,129,.12)'
                                    }}>
                                        {b.icon}
                                        <Typography sx={{ color: '#6B7280', fontWeight: 500, fontSize: '10px', whiteSpace: 'nowrap' }}>
                                            {b.label}
                                        </Typography>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>

            <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
        </Box>
    );
};

export default Login;